import { useState } from 'react'
import { inject, observer } from 'mobx-react'
import useMediaQuery from '@mui/material/useMediaQuery'

import { AdviceInvestmentTabs, AdviceAssetAllocation } from '../../../../components'
import {
  mapCategories,
  mapHoldingCategories,
  mapAccountCategories,
  mapOverallCategories,
} from '../../../../components/AdviceInvestments/mapBreakdown'
import { MOBILE_VIEWPORT } from '../../../../guided-toolbox/utils/breakpoints'
import { StepContent } from '../../elements'
import { buildInvestmentTabs } from './accounts'
import { HEADING_MESSAGE } from './headingMessage'
import {
  Heading,
  InvestmentsWrap,
  TabsWrap,
  AccountHeader,
  AccountName,
  Toggle,
  ToggleButton,
} from './style'

// Sentinel id for the portfolio-wide "Overall" tab (only present when there are
// multiple accounts).
const OVERALL_ID = '__overall__'

// Whether an account has no money in it. The engine still returns a recommended
// percentage mix for an unfunded account (how to invest it once funded), but
// every dollar figure is $0, so we show percentages only rather than "$0"
// down every row. Detected from the asset-class breakdown: holdings exist (there
// is a recommended mix) but their combined old + new dollars is zero.
const accountHasNoBalance = breakdown => {
  const assets = (breakdown?.assetClassBreakdown || []).flatMap(category => category.assets || [])
  if (assets.length === 0) {
    return false
  }
  const totalDollars = assets.reduce(
    (sum, asset) => sum + (asset.oldValue?.dollar || 0) + (asset.newValue?.dollar || 0),
    0
  )
  return totalDollars === 0
}

/**
 * Investments step of the step-by-step advice flow.
 *
 * Shows the current vs recommended asset allocation for the accounts
 * GuidedChoice advises. With more than one account it defaults to an "Overall"
 * portfolio summary tab and lets the user tab through each account; with a
 * single account it shows just that account. Each account can toggle between a
 * summary (asset classes) and details (investment-level) view when
 * investment-level advice is available.
 *
 * The step is only reachable when at least one advised (non-brokerage,
 * non-other-asset) account exists; Advice guards the route and skips traversal
 * otherwise (see hasInvestmentAccounts).
 */
const Investments = ({ store }) => {
  const isMobile = useMediaQuery(MOBILE_VIEWPORT)

  const activeCase = store.modifiedCase || store.recommendedCase
  const isModified = !!store.modifiedCase
  const allBreakdownDetailed = store.allBreakdownDetailed

  const tabs = buildInvestmentTabs({
    sortedAccounts: store.sortedAccounts,
    allBreakdown: allBreakdownDetailed,
    personId: store.person?.id,
    spouseId: store.spouse?.id,
  })

  const isMulti = tabs.length > 1
  const [activeAccount, setActiveAccount] = useState(isMulti ? OVERALL_ID : tabs[0]?.id)
  const [mode, setMode] = useState('summary')

  // The shell shows the loader and the route guard redirects when there are no
  // advised accounts; this just guards a transient render before data resolves.
  if (!activeCase || tabs.length === 0) {
    return null
  }

  const isOverall = isMulti && activeAccount === OVERALL_ID
  const currentTab = tabs.find(tab => tab.id === activeAccount) || tabs[0]
  const accountBreakdown = allBreakdownDetailed.find(account => account.name === activeAccount)

  // Individual account with nothing invested yet: show the recommended mix as
  // percentages only (the Overall tab aggregates real balances, so never this).
  const zeroBalance = !isOverall && accountHasNoBalance(accountBreakdown)

  // Investment-level advice is only offered when the "ALL" breakdown level is
  // enabled and this account actually has holdings data to show.
  const supportsDetails =
    !isOverall &&
    !!store.features.allBreakdownChartsEnabled &&
    (accountBreakdown?.investmentHoldings?.length || 0) > 0
  const showDetails = supportsDetails && mode === 'details'

  // Desktop switches granularity via the summary/details toggle (asset classes
  // vs holdings grouped under their class). Mobile always shows the asset-class
  // summary cards (sourced from the asset-class breakdown so classes without
  // itemized holdings still appear) with the holdings attached for expansion.
  const categories = isOverall
    ? mapOverallCategories(
        allBreakdownDetailed,
        tabs.map(tab => tab.id)
      )
    : isMobile
      ? mapAccountCategories(allBreakdownDetailed, activeAccount)
      : showDetails
        ? mapHoldingCategories(allBreakdownDetailed, activeAccount)
        : mapCategories(allBreakdownDetailed, activeAccount)

  const title = isOverall ? `Overall • ${tabs.length} accounts` : currentTab?.name

  const handleAccountChange = (name, account) => {
    setActiveAccount(account.id)
    setMode('summary')
  }

  const tabAccounts = [{ id: OVERALL_ID, name: 'Overall' }, ...tabs]

  return (
    <StepContent>
      <Heading>{HEADING_MESSAGE}</Heading>

      {isMulti && (
        <TabsWrap>
          <AdviceInvestmentTabs
            accounts={tabAccounts}
            activeAccount={activeAccount}
            onAccountChange={handleAccountChange}
          />
        </TabsWrap>
      )}

      <InvestmentsWrap>
        {/* Desktop individual-account header: name + summary/details toggle. */}
        {!isMobile && !isOverall && (
          <AccountHeader>
            <AccountName title={currentTab?.name}>{currentTab?.name}</AccountName>
            {supportsDetails && (
              <Toggle role='tablist'>
                <ToggleButton
                  type='button'
                  role='tab'
                  aria-selected={mode === 'summary'}
                  active={mode === 'summary'}
                  onClick={() => setMode('summary')}>
                  Summary
                </ToggleButton>
                <ToggleButton
                  type='button'
                  role='tab'
                  aria-selected={mode === 'details'}
                  active={mode === 'details'}
                  onClick={() => setMode('details')}>
                  Details
                </ToggleButton>
              </Toggle>
            )}
          </AccountHeader>
        )}

        {/* Remount when the tab changes so expanded cards do not carry between accounts. */}
        <AdviceAssetAllocation
          key={activeAccount}
          categories={categories}
          level={showDetails ? 'investment' : 'overall'}
          zeroBalance={zeroBalance}
          isModified={isModified}
          expandable={isMobile && supportsDetails}
          title={isMobile ? title : undefined}
        />
      </InvestmentsWrap>
    </StepContent>
  )
}

export default inject('store')(observer(Investments))
